import useCustomTranslation from "@/hooks/useTranslation";
import { pxToRem } from "@/util/common";
import { isDef } from "@/util/validate";
import { CenterPopup, Button, Input } from "antd-mobile";
import { isEmpty, isNumber } from "lodash";
import React, { useEffect } from "react";
import { createUseStyles } from "react-jss";

type TransactionType = "deposit" | "withdraw";
export type SubmitTransactionParams = {
	amount: number;
	remark?: string | null;
	address?: string | null;
};

export type TransactionParams = {
	min?: number | null;
	max?: number | null;
	tax?: number | null;
	balance?: number;
	feeRate?: number;
};
type TransactionPopupProp = TransactionParams & {
	type: TransactionType;
	visible: boolean;
	onClose?: () => void;
	onSubmit?: (
		type: TransactionType,
		params: SubmitTransactionParams
	) => Promise<any>;
};

const TransactionPopup: React.FC<TransactionPopupProp> = (props) => {
	const { t } = useCustomTranslation();
	const [amount, setAmount] = React.useState<string | null>();
	const [fee, setFee] = React.useState<string | null>("--");
	const [remark, setRemark] = React.useState<string | null>();
	const [errorMsg, setErrorMsg] = React.useState<string | null>();
	const classes = useStyles();

	useEffect(() => {
		if (isEmpty(amount)) {
			setErrorMsg(null);
			return;
		}
		let amountFloat = parseFloat(amount!);
		if (!isNumber(amountFloat)) {
			setErrorMsg(t("IncorrectInputFormat"));
			return;
		}

		if (
			isDef(props.max) &&
			isNumber(props.max) &&
			amountFloat > props.max
		) {
			setErrorMsg(`${t("AmountShouldLess")} ${props.max}`);
			return;
		}
		if (
			isDef(props.min) &&
			isNumber(props.min) &&
			amountFloat < props.min
		) {
			setErrorMsg(`${t("AmountShouldMore")} ${props.min}`);
			return;
		}

		if (
			isDef(props.balance) &&
			isNumber(props.balance) &&
			amountFloat > props.balance
		) {
			setErrorMsg(t("NotSufficientFunds"));
			return;
		}
		setErrorMsg(null);
	}, [amount]);

	useEffect(() => {
		// if (isEmpty(amount)) {
		// 	setFee("0");
		// 	return;
		// }
		// let amountFloat = parseFloat(amount!);
		// if (!isNumber(amountFloat)) {
		// 	setFee("0");
		// 	return;
		// }
		if (isDef(props.feeRate)) {
			// setFee((amountFloat * props.feeRate) / 100 + "");
			setFee(props.feeRate + "");
		}
	}, [amount]);

	const onSubmit = async () => {
		let submitPromise = props.onSubmit;
		if (!isDef(submitPromise) || !isDef(amount)) {
			return;
		}

		await submitPromise(props.type, { remark, amount: parseFloat(amount) });
	};

	return (
		<CenterPopup
			bodyStyle={{
				borderTopLeftRadius: pxToRem(20),
				borderTopRightRadius: pxToRem(20),
			}}
			style={{ "--max-width": pxToRem(346) }}
			maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
			bodyClassName={classes.popupContent}
			visible={props.visible}
			closeOnMaskClick
			destroyOnClose
			onClose={props.onClose}
		>
			<h3 className={classes.title}>
				{props.type == "deposit"
					? t("DepositAmount")
					: t("WithdrawAmount")}
				<em>{errorMsg}</em>
			</h3>

			<div
				className={`${classes.inputWrap} ${classes.amountInputWrap}`}
				style={{ marginTop: pxToRem(7) }}
			>
				<Input
					className={classes.input}
					type="number"
					value={amount ?? ""}
					onChange={(val) => setAmount(val)}
				/>
				<Button
					shape="rectangular"
					size="small"
					block
					className={classes.maxButton}
					onClick={() => {
						props.max &&
							setAmount(
								Math.min(props.balance ?? 0, props.max) + ""
							);
					}}
				>
					{t("Max")}
				</Button>
			</div>

			<p className={classes.info} style={{ marginTop: pxToRem(9) }}>
				{t("Balance")} <em>{props.balance || 0} USDT</em>
			</p>
			<p className={classes.info} style={{ marginTop: pxToRem(3) }}>
				{t("EstimatedFee")} <em>{fee} USDT</em>
			</p>
			<p className={classes.info} style={{ marginTop: pxToRem(22) }}>
				{t("Remark")} ({t("Optional")})
			</p>
			<div
				className={`${classes.inputWrap}`}
				style={{ marginTop: pxToRem(8) }}
			>
				<Input
					className={classes.input}
					onChange={(val) => setRemark(val)}
				/>
			</div>

			<Button
				className={classes.btn}
				style={{ marginTop: pxToRem(24) }}
				shape="rounded"
				disabled={!isEmpty(errorMsg) || isEmpty(amount)}
				onClick={onSubmit}
			>
				{props.type == "deposit" ? t("Deposit") : t("Whithdraw")}
			</Button>
		</CenterPopup>
	);
};

export default TransactionPopup;

const useStyles = createUseStyles({
	popupContent: {
		backgroundColor: "#1E212A",
		color: "#868FA8",
		border: "1px solid #2F3A62",
		padding: {
			left: pxToRem(16),
			right: pxToRem(16),
			top: pxToRem(20),
			bottom: pxToRem(47),
		},
	},
	margin18: {
		marginTop: pxToRem(18),
	},
	title: {
		fontSize: pxToRem(14),
		fontWeight: 500,
		color: "#fff",
		display: "flex",
		justifyContent: "space-between",
		"& > em": {
			color: "#FF6161",
		},
	},
	inputWrap: {
		border: "1px solid #2F3A62",
		backgroundColor: "#181A22",
		borderRadius: pxToRem(8),
		height: pxToRem(50),
		padding: {
			left: pxToRem(10),
			right: pxToRem(6),
		},
		"&.focus": {
			borderColor: "#4C66C7",
		},
		"&.warn": {
			borderColor: "#FF6161",
		},
	},
	amountInputWrap: {
		display: "flex",
		alignItems: "center",
	},
	input: {
		flex: 1,
		height: "100%",
		"--color": "#fff",
	},
	maxButton: {
		width: pxToRem(55),
		height: pxToRem(36),
		"--border-style": "none",
		backgroundColor: "#4C66C7",
		fontWeight: 500,
		fontSize: pxToRem(16),
		borderRadius: pxToRem(6),
		color: "#fff",
	},
	info: {
		fontSize: pxToRem(14),
		fontWeight: 500,
		"& > em": {
			color: "#fff",
		},
	},
	btn: {
		width: pxToRem(292),
		height: pxToRem(50),
		fontWeight: 700,
		fontSize: pxToRem(16),
		color: "#fff",
		"--border-color": "#4C66C7",
		backgroundColor: "#182B64",
	},
});
